// pages/admin/ContactList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import {
  MdSearch, MdMail, MdPhone, MdDelete, MdVisibility,
  MdMarkEmailRead, MdMailOutline, MdClose, MdReply, MdChat,
} from 'react-icons/md';

export default function ContactEnquiry() {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  const [messages, setMessages] = useState([]);
  const [statusCounts, setStatusCounts] = useState({
    new: 0, read: 0, replied: 0, closed: 0,
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  // View modal
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statusDraft, setStatusDraft] = useState('');
  const [notesDraft, setNotesDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;

      const res = await axios.get(
        `${base_url}/api/v1/contact-messages/admin/list`,
        { params }
      );

      if (res.data.success) {
        setMessages(res.data.data?.data || []);
        setStatusCounts(
          res.data.data?.statusCounts || { new: 0, read: 0, replied: 0, closed: 0 }
        );
        setPagination(
          res.data.data?.pagination || { total: 0, totalPages: 0 }
        );
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [base_url, page, search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Search debounce
  useEffect(() => {
    const t = setTimeout(() => {
      if (page !== 1) setPage(1);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openDetails = async (id) => {
    setShowModal(true);
    setModalLoading(true);
    setSelected(null);
    try {
      const res = await axios.get(
        `${base_url}/api/v1/contact-messages/admin/${id}`
      );
      if (res.data.success) {
        setSelected(res.data.data);
        setStatusDraft(res.data.data.status || 'new');
        setNotesDraft(res.data.data.notes || '');
        // refresh count if status changed to 'read'
        load();
      }
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to load');
    } finally {
      setModalLoading(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.patch(
        `${base_url}/api/v1/contact-messages/admin/${selected.id}`,
        { status: statusDraft, notes: notesDraft }
      );
      setSelected((p) => ({ ...p, status: statusDraft, notes: notesDraft }));
      load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete message from "${name}"?`)) return;
    setActionLoading((p) => ({ ...p, [id]: true }));
    try {
      await axios.delete(`${base_url}/api/v1/contact-messages/admin/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      setPagination((p) => ({ ...p, total: p.total - 1 }));
      load();
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to delete');
    } finally {
      setActionLoading((p) => ({ ...p, [id]: false }));
    }
  };

  const statusBadge = (s) => {
    const map = {
      new: 'primary',
      read: 'info',
      replied: 'success',
      closed: 'secondary',
    };
    return map[s] || 'secondary';
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '-';

  return (
    <div className="page-wrapper">
      <div className="container-fluid mt-3">
        <div className="card shadow border-0">
          {/* Header */}
          <div className="card-header bg-white">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h4 className="fw-bold mb-0">
                <MdChat className="me-2 text-primary" />
                Contact Messages
                <span className="badge bg-primary ms-2">
                  {pagination.total}
                </span>
              </h4>
            </div>

            {/* Status pills */}
            <div className="d-flex flex-wrap gap-2 mb-3">
              <button
                className={`btn btn-sm ${
                  statusFilter === '' ? 'btn-primary' : 'btn-outline-primary'
                }`}
                onClick={() => { setStatusFilter(''); setPage(1); }}
              >
                All ({pagination.total})
              </button>
              <button
                className={`btn btn-sm ${
                  statusFilter === 'new' ? 'btn-primary' : 'btn-outline-primary'
                }`}
                onClick={() => { setStatusFilter('new'); setPage(1); }}
              >
                <MdMailOutline /> New ({statusCounts.new})
              </button>
              <button
                className={`btn btn-sm ${
                  statusFilter === 'read' ? 'btn-info' : 'btn-outline-info'
                }`}
                onClick={() => { setStatusFilter('read'); setPage(1); }}
              >
                <MdMarkEmailRead /> Read ({statusCounts.read})
              </button>
              <button
                className={`btn btn-sm ${
                  statusFilter === 'replied' ? 'btn-success' : 'btn-outline-success'
                }`}
                onClick={() => { setStatusFilter('replied'); setPage(1); }}
              >
                <MdReply /> Replied ({statusCounts.replied})
              </button>
              <button
                className={`btn btn-sm ${
                  statusFilter === 'closed' ? 'btn-secondary' : 'btn-outline-secondary'
                }`}
                onClick={() => { setStatusFilter('closed'); setPage(1); }}
              >
                <MdClose /> Closed ({statusCounts.closed})
              </button>
            </div>

            <div className="row g-3">
              <div className="col-md-12">
                <div className="input-group">
                  <span className="input-group-text"><MdSearch /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search name, email, phone, subject or message..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 60 }}>#</th>
                    <th>From</th>
                    <th>Contact</th>
                    <th>Subject</th>
                    <th>Message</th>
                    <th style={{ width: 100 }}>Status</th>
                    <th>Date</th>
                    <th style={{ width: 120 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [...Array(6)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(8)].map((__, j) => (
                          <td key={j}><Skeleton /></td>
                        ))}
                      </tr>
                    ))
                  ) : messages.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5 text-muted">
                        <MdMailOutline size={40} />
                        <div className="mt-2">No messages yet</div>
                      </td>
                    </tr>
                  ) : (
                    messages.map((m, idx) => (
                      <tr
                        key={m.id}
                        style={{
                          background: m.status === 'new' ? '#f0f7ff' : 'white',
                          fontWeight: m.status === 'new' ? 500 : 400,
                        }}
                      >
                        <td>{(page - 1) * limit + idx + 1}</td>

                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                background: '#01457B',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {(m.name || 'U').charAt(0).toUpperCase()}
                            </div>
                            <strong>{m.name}</strong>
                          </div>
                        </td>

                        <td>
                          <div className="small">
                            <MdMail /> {m.email}
                          </div>
                          {m.phone && (
                            <div className="small text-muted">
                              <MdPhone /> {m.phone}
                            </div>
                          )}
                        </td>

                        <td>
                          {m.subject ? (
                            <span>{m.subject}</span>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>

                        <td style={{ maxWidth: 320 }}>
                          <div
                            style={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              fontSize: 13.5,
                            }}
                          >
                            {m.message}
                          </div>
                        </td>

                        <td>
                          <span
                            className={`badge bg-${statusBadge(m.status)} text-uppercase`}
                          >
                            {m.status || 'new'}
                          </span>
                        </td>

                        <td className="small">{formatDate(m.created_at)}</td>

                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => openDetails(m.id)}
                              title="View & manage"
                            >
                              <MdVisibility />
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              disabled={actionLoading[m.id]}
                              onClick={() => handleDelete(m.id, m.name)}
                              title="Delete"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                  Showing {Math.min((page - 1) * limit + 1, pagination.total)} -{' '}
                  {Math.min(page * limit, pagination.total)} of {pagination.total}
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    Page <strong>{page}</strong> of{' '}
                    <strong>{pagination.totalPages}</strong>
                  </span>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowModal(false);
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selected?.subject || 'Message Details'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>

              <div className="modal-body">
                {modalLoading && (
                  <div className="text-center py-4">
                    <Skeleton count={6} />
                  </div>
                )}

                {!modalLoading && selected && (
                  <>
                    {/* Sender info */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <small className="text-muted d-block">From</small>
                        <strong>{selected.name}</strong>
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted d-block">Received</small>
                        <strong>{formatDate(selected.created_at)}</strong>
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted d-block">Email</small>
                        <a href={`mailto:${selected.email}`}>{selected.email}</a>
                      </div>
                      <div className="col-md-6">
                        <small className="text-muted d-block">Phone</small>
                        {selected.phone ? (
                          <a href={`tel:${selected.phone}`}>{selected.phone}</a>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </div>
                    </div>

                    {/* Message body */}
                    <div className="mb-4">
                      <small className="text-muted d-block mb-1">Message</small>
                      <div
                        className="border rounded p-3"
                        style={{
                          background: '#f9fafb',
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.65,
                        }}
                      >
                        {selected.message}
                      </div>
                    </div>

                    {/* Status & notes */}
                    <div className="row g-3">
                      <div className="col-md-4">
                        <label className="form-label fw-bold small">Status</label>
                        <select
                          className="form-select"
                          value={statusDraft}
                          onChange={(e) => setStatusDraft(e.target.value)}
                        >
                          <option value="new">New</option>
                          <option value="read">Read</option>
                          <option value="replied">Replied</option>
                          <option value="closed">Closed</option>
                        </select>
                      </div>
                      <div className="col-md-8">
                        <label className="form-label fw-bold small">
                          Internal Notes (not visible to user)
                        </label>
                        <textarea
                          className="form-control"
                          rows="2"
                          placeholder="Optional notes..."
                          value={notesDraft}
                          onChange={(e) => setNotesDraft(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="d-flex gap-2 mt-3 flex-wrap">
                      <a
                        href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || 'Your enquiry')}`}
                        className="btn btn-sm btn-outline-success"
                        onClick={() => setStatusDraft('replied')}
                      >
                        <MdReply /> Reply via Email
                      </a>
                      {selected.phone && (
                        <a
                          href={`tel:${selected.phone}`}
                          className="btn btn-sm btn-outline-primary"
                        >
                          <MdPhone /> Call
                        </a>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
                {selected && (
                  <button
                    className="btn btn-primary"
                    disabled={saving}
                    onClick={handleSaveStatus}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}